(() => {
  if (customElements.get('rdb-hero-benefits')) return;
  customElements.define('rdb-hero-benefits', class extends HTMLElement {
    connectedCallback() {
      clearTimeout(this.rotation);
      clearTimeout(this.settle);
      this.events?.abort();
      this.events = new AbortController();
      const on = (node, name, handler, options = {}) => node.addEventListener(name, handler, { ...options, signal: this.events.signal });
      this.track = this.querySelector('ul');
      this.track.classList.remove('is-dragging');
      this.track.querySelectorAll('[data-benefit-clone]').forEach((item) => item.remove());
      this.items = [...this.track.children];
      this.status = this.querySelector('[role="status"]');
      this.compact = matchMedia('(max-width: 820px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.current = 0;
      this.deliberate = false;
      this.hovered = false;
      this.visible = false;
      this.press = null;
      this.pointerActive = false;
      this.looping = false;
      this.width = 0;
      const previous = this.querySelector('.rdb-benefits__previous');
      const next = this.querySelector('.rdb-benefits__next');
      previous.hidden = next.hidden = false;
      on(previous, 'click', () => this.move(this.current - 1));
      on(next, 'click', () => this.move(this.current + 1));
      on(this.track, 'keydown', (event) => {
        const rtl = getComputedStyle(this.track).direction === 'rtl';
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        this.move(event.key === 'Home' ? 0 : event.key === 'End' ? this.items.length - 1 : this.current + ((event.key === 'ArrowRight') !== rtl ? 1 : -1));
      });
      on(this.track, 'scroll', () => {
        clearTimeout(this.settle);
        this.settle = setTimeout(() => this.update(true), 120);
      }, { passive: true });
      on(this.track, 'scrollend', () => this.update(true));
      on(this.track, 'pointerdown', (event) => {
        this.takeControl();
        this.pointerActive = true;
        if (event.pointerType !== 'mouse' || event.button !== 0 || !this.compact.matches) return;
        this.press = { id: event.pointerId, x: event.clientX, left: this.track.scrollLeft };
      });
      on(window, 'pointermove', (event) => {
        if (!this.press || this.press.id !== event.pointerId) return;
        const distance = event.clientX - this.press.x;
        if (Math.abs(distance) < 5 && !this.track.classList.contains('is-dragging')) return;
        this.track.classList.add('is-dragging');
        this.track.setPointerCapture(event.pointerId);
        event.preventDefault();
        this.track.scrollLeft = this.press.left - distance;
      }, { passive: false });
      const release = () => {
        this.pointerActive = false;
        if (!this.press) {
          this.update();
          return;
        }
        const id = this.press.id;
        const position = Math.round(Math.abs(this.track.scrollLeft) / this.track.clientWidth);
        this.press = null;
        if (this.track.hasPointerCapture(id)) this.track.releasePointerCapture(id);
        this.scrollToSlide(position, this.reduced.matches ? 'instant' : 'smooth');
        this.update();
      };
      on(window, 'pointerup', release);
      on(window, 'pointercancel', release);
      on(window, 'blur', release);
      on(this, 'focusin', () => this.takeControl());
      on(this.track, 'wheel', () => this.takeControl(), { passive: true });
      on(this, 'pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = true;
        this.schedule();
      });
      on(this, 'pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = false;
        this.schedule();
      });
      on(document, 'visibilitychange', () => this.schedule());
      on(this.reduced, 'change', () => this.schedule());
      on(this.compact, 'change', () => this.measure());
      this.visibilityObserver?.disconnect();
      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
        this.schedule();
      }, { threshold: [0, 0.5] });
      this.visibilityObserver.observe(this);
      this.observer?.disconnect();
      this.observer = new ResizeObserver(() => this.measure());
      this.observer.observe(this);
      this.measure();
    }

    measure() {
      this.closest('rdb-opening')?.style.setProperty('--opening-benefits-height', Math.ceil(this.getBoundingClientRect().height) + 'px');
      this.track.tabIndex = this.compact.matches ? 0 : -1;
      const looping = this.compact.matches && this.items.length > 1;
      const changed = looping !== this.looping;
      if (changed) {
        this.track.querySelectorAll('[data-benefit-clone]').forEach((item) => item.remove());
        this.looping = looping;
        if (looping) {
          const clone = (item) => {
            const copy = item.cloneNode(true);
            copy.dataset.benefitClone = '';
            copy.setAttribute('aria-hidden', 'true');
            copy.inert = true;
            return copy;
          };
          this.track.prepend(clone(this.items[this.items.length - 1]));
          this.track.append(clone(this.items[0]));
        }
      }
      if (changed || this.width !== this.track.clientWidth) {
        this.width = this.track.clientWidth;
        if (!looping) this.current = 0;
        this.scrollToSlide(looping ? this.current + 1 : 0, 'instant');
      }
      this.update();
      this.schedule();
    }

    takeControl() {
      this.deliberate = true;
      clearTimeout(this.rotation);
    }

    schedule() {
      clearTimeout(this.rotation);
      if (!this.isConnected || !this.compact.matches || this.reduced.matches || this.deliberate || this.hovered || !this.visible || document.hidden || this.items.length < 2) return;
      this.rotation = setTimeout(() => {
        this.move(this.current + 1, false);
        this.schedule();
      }, 3000);
    }

    move(index, manual = true) {
      if (manual) this.takeControl();
      const selected = (index + this.items.length) % this.items.length;
      const position = this.looping ? index < 0 ? 0 : index >= this.items.length ? this.items.length + 1 : selected + 1 : selected;
      this.scrollToSlide(position, this.reduced.matches ? 'instant' : 'smooth');
    }

    scrollToSlide(position, behavior) {
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      this.track.scrollTo({ left: position * this.track.clientWidth * (rtl ? -1 : 1), behavior });
    }

    update(settled = false) {
      if (!this.track.clientWidth) return;
      const position = Math.abs(this.track.scrollLeft) / this.track.clientWidth;
      const slide = Math.round(position);
      this.current = this.looping ? (slide - 1 + this.items.length) % this.items.length : Math.min(this.items.length - 1, slide);
      if (settled && !this.pointerActive && this.track.classList.contains('is-dragging') && Math.abs(position - slide) >= 0.01) {
        this.scrollToSlide(slide, this.reduced.matches ? 'instant' : 'smooth');
      }
      if (this.looping && !this.pointerActive && Math.abs(position - slide) < 0.01 && (slide === 0 || slide === this.items.length + 1)) {
        this.scrollToSlide(this.current + 1, 'instant');
      }
      if (!this.pointerActive && Math.abs(position - slide) < 0.01) this.track.classList.remove('is-dragging');
      this.dataset.activeSlide = String(this.current);
      if (this.deliberate && this.compact.matches) this.status.textContent = this.dataset.statusTemplate.replace('[current]', String(this.current + 1)) + ': ' + this.items[this.current].innerText.trim();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.observer?.disconnect();
      this.visibilityObserver?.disconnect();
      clearTimeout(this.settle);
      clearTimeout(this.rotation);
    }
  });
})();
